import { isAnyOf } from '@reduxjs/toolkit';
import { isDefined, isNotDefined } from '@sgme/fp';

import { getMaxDate } from '@/api/dates/utils.ts';
import { getSignalRConnector } from '@/api/signalR/SignalRConnector.ts';
import { selectGetCurrentUser } from '@/api/user/index.ts';
import type { StartAppListening } from '@/store/listenerMiddleware.ts';
import { changeClient } from '@/store/tabs/slice.ts';
import { matchCheckTargetAccumulatorFulfilled, targetAccumulatorApi } from '@/store/targetAccumulator/api/index.ts';
import { getTargetAccumulatorRfsId } from '@/store/targetAccumulator/selectors.ts';
import { targetAccumulatorSlice } from '@/store/targetAccumulator/slice.ts';
import type { FxTargetAccumulator } from '@/store/targetAccumulator/state.ts';
import { cancelAllTargetAccumulatorRfsThunk } from '@/store/targetAccumulator/thunk.ts';
import { selectTileClientId } from '@/store/tiles/selectors.ts';

const heartbeatListenersByRfsId: Record<string, (rfsId: string) => void> = {};

export const registerRfsTargetAccumulatorListeners = (startAppListening: StartAppListening) => {
  startAppListening({
    matcher: matchCheckTargetAccumulatorFulfilled,
    effect: async (action, api) => {
      const { dispatch, getState } = api;
      const currentState = getState();

      const requestPayload = action.meta.arg.originalArgs;
      const tradeCaptureResponse = action.payload;

      const tileId = requestPayload.tileId;

      const tile = targetAccumulatorSlice.selectors.getTargetAccumulator(api.getState(), tileId);
      const clientId = selectTileClientId(currentState, tileId);

      if (isNotDefined(tile) || isDefined(tile.execution) || isNotDefined(clientId)) {
        return;
      }

      const { isReadyToPrice, isPriceObsolete, isProductObsolete } = getTradeCaptureStatus(
        tradeCaptureResponse.tradeCapture,
      );

      const currentRfsId = isDefined(tile) ? getTargetAccumulatorRfsId(tile) : undefined;

      // ON A UN CURRENT RFS ID
      if (isDefined(currentRfsId)) {
        if (isReadyToPrice && !isPriceObsolete && !isProductObsolete) {
          // When TC replies ready to price but price/product aren't obsolete, we do nothing
          return;
        }

        if (isProductObsolete && isDefined(currentRfsId)) {
          await dispatch(
            targetAccumulatorApi.endpoints.cancelTargetAccumulatorRfs.initiate({
              rfsId: currentRfsId,
              tileId,
              removeQuote: true,
            }),
          );
        }
      }
    },
  });

  startAppListening({
    matcher: targetAccumulatorApi.endpoints.requestTargetAccumulatorRfs.matchFulfilled,
    effect: async (action, api) => {
      const { rfsId, tileId } = action.meta.arg.originalArgs;
      const signalRConnector = getSignalRConnector();

      function onHeartbeatTimeout(id: string) {
        if (id !== rfsId) {
          return;
        }
        api.dispatch(targetAccumulatorApi.endpoints.cancelTargetAccumulatorRfs.initiate({ rfsId, tileId }));
      }

      heartbeatListenersByRfsId[rfsId] = onHeartbeatTimeout;
      signalRConnector.onHeartbeatTimeout(rfsId, onHeartbeatTimeout);
    },
  });

  startAppListening({
    matcher: targetAccumulatorApi.endpoints.cancelTargetAccumulatorRfs.matchPending,
    effect: async (action) => {
      const { rfsId } = action.meta.arg.originalArgs;

      const listener = heartbeatListenersByRfsId[rfsId];
      if (listener) {
        getSignalRConnector().offHeartbeatTimeout(rfsId, listener);
        delete heartbeatListenersByRfsId[rfsId];
      }
    },
  });

  startAppListening({
    matcher: targetAccumulatorApi.endpoints.executeTargetAccumulatorRfs.matchPending,
    effect: async (action) => {
      const { rfsId } = action.meta.arg.originalArgs;

      const listener = heartbeatListenersByRfsId[rfsId];
      if (listener) {
        getSignalRConnector().offHeartbeatTimeout(rfsId, listener);
        delete heartbeatListenersByRfsId[rfsId];
      }
    },
  });

  startAppListening({
    matcher: changeClient.match,
    effect: async (_, api) => {
      const { dispatch, getState } = api;
      const state = getState();

      const targetAccumulator = Object.entries(state.targetAccumulator);
      if (targetAccumulator?.length > 0) {
        targetAccumulator.forEach(async ([tileId, targetAccumulator]) => {
          const clientId = selectTileClientId(state, tileId);
          const currentUserCache = selectGetCurrentUser(state).data?.clients;
          const maxDate = await getMaxDate(
            clientId,
            currentUserCache,
            dispatch,
            targetAccumulator.properties.currencyPair,
          );
          dispatch(targetAccumulatorSlice.actions.setTargetAccumulatorMaxDate({ tileId, maxDate }));
        });
      }

      await api.dispatch(cancelAllTargetAccumulatorRfsThunk());
    },
  });

  startAppListening({
    matcher: isAnyOf(
      targetAccumulatorSlice.actions.changeClient.match,
      targetAccumulatorSlice.actions.targetAccumulatorClientIdUpdated.match,
    ),
    effect: async (action, api) => {
      const tileId =
        targetAccumulatorSlice.actions.changeClient.match(action) ||
        targetAccumulatorSlice.actions.targetAccumulatorClientIdUpdated.match(action)
          ? action.payload.tileId
          : undefined;

      if (isNotDefined(tileId)) {
        return;
      }

      if (
        targetAccumulatorSlice.actions.changeClient.match(action) &&
        api.getOriginalState().targetAccumulator[tileId]?.clientId === action.payload.clientId
      ) {
        return;
      }

      const targetAccumulatorTile = targetAccumulatorSlice.selectors.getTargetAccumulator(api.getState(), tileId);
      if (isNotDefined(targetAccumulatorTile)) {
        return;
      }

      const rfs = targetAccumulatorTile.properties.rfs;

      if (rfs.status === 'AWAITING' || rfs.status === 'PRICING') {
        await api.dispatch(
          targetAccumulatorApi.endpoints.cancelTargetAccumulatorRfs.initiate({
            tileId: tileId,
            rfsId: rfs.rfsId,
            removeQuote: true,
          }),
        );
      }
    },
  });
};

interface TcData {
  isReadyToPrice: boolean;
  isProductObsolete: boolean;
  isPriceObsolete: boolean;
}

function getTradeCaptureStatus(tradeCapture: FxTargetAccumulator['tradeCapture']): TcData {
  if (tradeCapture?.status !== 'CHECKED') {
    return {
      isReadyToPrice: false,
      isProductObsolete: false,
      isPriceObsolete: false,
    };
  }

  return {
    isReadyToPrice: tradeCapture?.isReadyToPrice,
    isProductObsolete: tradeCapture?.isProductObsolete,
    isPriceObsolete: tradeCapture?.isPriceObsolete,
  };
}
